import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { tokens } from '../../theme';
import 'mathlive';
import {
  defaultMultilineConfig,
  hydrateStudentAnswer,
  updateStudentRow,
  addStudentRow,
  removeStudentRow,
  getRowLabel,
  getRowType,
  ROW_TYPES,
} from './multilineUtils';

/**
 * MultilineStudent Component
 * Student interface for answering multiline-based homework questions.
 * Renders dynamic rows of mathlive or text inputs.
 */
const MultilineStudent = ({
  config = defaultMultilineConfig,
  value = null,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [answer, setAnswer] = useState(() => hydrateStudentAnswer(value, config));

  // Sync with external value changes only — re-running on `config` identity
  // would wipe locally-typed rows whenever the parent re-renders. We do
  // hydrate against `config.minRows` though, so an admin bumping the row
  // count after the student saved a shorter answer fills out the visible
  // rows on next render.
  useEffect(() => {
    if (value && value.rows && value.rows.length > 0) {
      setAnswer(hydrateStudentAnswer(value, config));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, config.minRows]);

  const handleRowChange = (rowId, newValue) => {
    const updated = updateStudentRow(answer, rowId, newValue);
    setAnswer(updated);
    onChange?.(updated);
  };

  const handleAddRow = () => {
    const updated = addStudentRow(answer, config);
    setAnswer(updated);
    onChange?.(updated);
  };

  const handleRemoveRow = (rowId) => {
    const updated = removeStudentRow(answer, rowId, config);
    setAnswer(updated);
    onChange?.(updated);
  };

  // For allowTextInput rows, write both the active type and value in one
  // update so the row's type tracks whichever input the student is using.
  const writeRowTyped = (rowId, type, newValue) => {
    const updated = {
      ...answer,
      rows: (answer.rows || []).map(r =>
        r.id === rowId ? { ...r, type, value: newValue } : r
      ),
    };
    setAnswer(updated);
    onChange?.(updated);
  };

  const dualInput = config.inputType === 'mathlive' && config.allowTextInput === true;

  const maxRows = config.maxRows || 0;
  const minRows = config.minRows || 1;
  const canAdd = config.studentCanAddRows !== false && (maxRows === 0 || (answer.rows || []).length < maxRows);
  const canRemove = config.studentCanAddRows !== false && (answer.rows || []).length > minRows;

  return (
    <Box sx={{ width: '100%' }}>
      {(answer.rows || []).map((row, index) => {
        const label = getRowLabel(config, index);
        const rowType = getRowType(row, config);
        const isMath = rowType === ROW_TYPES.MATH;
        const currentValue = row.value || '';
        const hasValue = !!String(currentValue).trim();
        // Mirrors the plain mathlive `allowTextInput` pattern in
        // TextbookAndQuestions.jsx: typing in one input locks the other
        // until the active one is cleared. Type is inferred from where the
        // student wrote.
        const mathReadOnly = disabled || (dualInput && hasValue && !isMath);
        const textDisabled = disabled || (dualInput && hasValue && isMath);

        return (
          <Box
            key={row.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              flexWrap: dualInput ? 'wrap' : 'nowrap',
            }}
          >
            {/* Row label */}
            {label && (
              <Typography
                variant="body2"
                sx={{ minWidth: 30, fontWeight: 'bold', color: colors.grey[300] }}
              >
                {label}
              </Typography>
            )}

            {dualInput ? (
              <>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <math-field
                    aria-label={`Row ${index + 1} math answer`}
                    onInput={(evt) => {
                      if (mathReadOnly) return;
                      writeRowTyped(row.id, ROW_TYPES.MATH, evt.target.value);
                    }}
                    read-only={mathReadOnly ? true : undefined}
                    // Imperatively sync the math-field's internal value to
                    // the prop — React doesn't push attribute changes into a
                    // web component after mount, so without this the math
                    // box would show stale content when the row switches
                    // type (or when a saved value is loaded).
                    ref={(el) => {
                      const expected = isMath ? currentValue : '';
                      if (el && el.getValue && el.getValue() !== expected) {
                        el.setValue(expected);
                      }
                    }}
                    style={{
                      width: '100%',
                      minHeight: '40px',
                      fontSize: '16px',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '4px',
                      padding: '4px 8px',
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      opacity: mathReadOnly && !disabled ? 0.5 : disabled ? 0.5 : 1,
                      pointerEvents: disabled ? 'none' : 'auto',
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ mx: 0.5 }}>or</Typography>
                <TextField
                  value={!isMath ? currentValue : ''}
                  onChange={(e) => writeRowTyped(row.id, ROW_TYPES.TEXT, e.target.value)}
                  disabled={textDisabled}
                  size="small"
                  sx={{ flex: 1, minWidth: 160 }}
                  placeholder="Text answer"
                  inputProps={{ 'aria-label': `Row ${index + 1} text answer` }}
                />
              </>
            ) : isMath ? (
              <Box sx={{ flex: 1 }}>
                <math-field
                  aria-label={`Row ${index + 1} answer`}
                  onInput={(evt) => {
                    if (!disabled) {
                      handleRowChange(row.id, evt.target.value);
                    }
                  }}
                  // Imperative sync — see MultilineReadOnly for the
                  // rationale. Without this, an externally-changed value
                  // (e.g., loading a saved answer) won't display correctly.
                  ref={(el) => {
                    const expected = row.value || '';
                    if (el && el.getValue && el.getValue() !== expected) {
                      el.setValue(expected);
                    }
                  }}
                  style={{
                    width: '100%',
                    minHeight: '40px',
                    fontSize: '16px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '4px',
                    padding: '4px 8px',
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    opacity: disabled ? 0.5 : 1,
                    pointerEvents: disabled ? 'none' : 'auto',
                  }}
                />
              </Box>
            ) : (
              <TextField
                value={row.value || ''}
                onChange={(e) => {
                  if (!disabled) {
                    handleRowChange(row.id, e.target.value);
                  }
                }}
                disabled={disabled}
                size="small"
                fullWidth
                placeholder={`Answer ${index + 1}`}
                inputProps={{ 'aria-label': `Row ${index + 1} answer` }}
              />
            )}

            {/* Remove button */}
            {canRemove && !disabled && (
              <IconButton
                size="small"
                onClick={() => handleRemoveRow(row.id)}
                sx={{ color: colors.grey[400] }}
              >
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      })}

      {/* Add row button */}
      {canAdd && !disabled && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          size="small"
          sx={{ mt: 0.5 }}
        >
          Add Row
        </Button>
      )}
    </Box>
  );
};

export default MultilineStudent;
